import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { portfolioData, websitePortfolioData } from "@/constants";
import { PortfolioDataItem } from "@/types/types";
import CreativeGallery from "@/components/portfolioComponents/CreativeGallery";
import VideosFeed from "@/components/portfolioComponents/VideosFeed";
import CreativeLightbox from "@/components/portfolioComponents/CreativeLightbox";
import CategoryBrowser from "@/components/portfolioComponents/CategoryBrowser";
import PortfolioLanding from "@/components/portfolioComponents/PortfolioLanding";
import PortfolioNav from "@/components/portfolioComponents/PortfolioNav";
import WebsiteGallery from "@/components/portfolioComponents/WebsiteGallery";
import LandingNav from "@/components/portfolioComponents/LandingNav";
import { ArrowLeft } from "lucide-react";
import {
  getPortfolioBrandPath,
  getPortfolioCategoryPath,
  getPortfolioMediaPath,
  getPortfolioMediaSlug,
  getPortfolioSlug,
} from "@/lib/portfolioLinks";

type ViewMode = "landing" | "creatives" | "videos" | "websites";
type PortfolioSection = Exclude<ViewMode, "landing">;

export interface PortfolioItem {
  type: PortfolioDataItem["type"];
  title: string;
  categoryDesc: string;
  brand: string;
  description: string;
  src: string;
  mediaIndex: number;
}

const getItemsForGroup = (group: PortfolioDataItem): PortfolioItem[] => {
  return group.media.map((src, mediaIndex) => ({
    type: group.type,
    title: group.title,
    categoryDesc: group.categoryDesc,
    brand: group.brand,
    description: group.description,
    src,
    mediaIndex,
  }));
};

const Portfolio = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [view, setView] = useState<ViewMode>("landing");
  const [selectedCreative, setSelectedCreative] =
    useState<PortfolioItem | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<PortfolioDataItem | null>(
    null,
  );
  const [highlightedCategory, setHighlightedCategory] = useState<string | null>(
    null,
  );
  const [initialVideoIndex, setInitialVideoIndex] = useState(0);
  const [tutorialSeen, setTutorialSeen] = useState<
    Record<PortfolioSection, boolean>
  >({
    creatives: false,
    videos: false,
    websites: false,
  });

  const groups = useMemo(
    () => [
      ...(portfolioData as PortfolioDataItem[]),
      ...(websitePortfolioData as PortfolioDataItem[]),
    ],
    [],
  );

  const creativeGroups = useMemo(
    () => groups.filter((item) => item.type === "image"),
    [groups],
  );

  const videoGroups = useMemo(
    () => groups.filter((item) => item.type === "video"),
    [groups],
  );

  const websiteGroups = useMemo(
    () => groups.filter((item) => item.type === "website"),
    [groups],
  );

  const selectedItems = useMemo(
    () => (selectedGroup ? getItemsForGroup(selectedGroup) : []),
    [selectedGroup],
  );

  useEffect(() => {
    const routeState = location.state as
      | { highlightedCategory?: string }
      | null;
    const queryCategorySlug = new URLSearchParams(location.search).get(
      "category",
    );
    const segments = location.pathname
      .replace(/^\/portfolio\/?/, "")
      .split("/")
      .filter(Boolean)
      .map(decodeURIComponent);

    if (segments.length === 0) {
      setView("landing");
      setSelectedGroup(null);
      setSelectedCreative(null);
      setHighlightedCategory(null);
      return;
    }

    const [typeSegment, brandSlug, mediaSlug] = segments;
    const type =
      typeSegment === "image" ||
      typeSegment === "video" ||
      typeSegment === "website"
      ? typeSegment
      : null;

    if (!type) {
      navigate("/portfolio", { replace: true });
      return;
    }

    const nextView: PortfolioSection =
      type === "image" ? "creatives" : type === "video" ? "videos" : "websites";
    setView(nextView);
    setSelectedCreative(null);
    const queryCategory = queryCategorySlug
      ? groups.find(
          (item) =>
            item.type === type && getPortfolioSlug(item.title) === queryCategorySlug,
        )?.title
      : null;
    setHighlightedCategory(
      routeState?.highlightedCategory || queryCategory || null,
    );

    if (!brandSlug) {
      setSelectedGroup(null);
      return;
    }

    const group = groups.find(
      (item) => item.type === type && getPortfolioSlug(item.brand) === brandSlug,
    );

    if (!group) {
      navigate(`/portfolio/${type}`, { replace: true });
      return;
    }

    setSelectedGroup(group);

    if (!mediaSlug) {
      setInitialVideoIndex(0);
      return;
    }

    const mediaIndex = group.media.findIndex(
      (src) => getPortfolioMediaSlug(src) === mediaSlug,
    );

    if (mediaIndex < 0) {
      navigate(getPortfolioBrandPath(group), { replace: true });
      return;
    }

    if (type === "image") {
      setSelectedCreative(getItemsForGroup(group)[mediaIndex]);
    } else if (type === "video") {
      setInitialVideoIndex(mediaIndex);
    }
  }, [groups, location.pathname, location.search, location.state, navigate]);

  const handleViewChange = (nextView: PortfolioSection) => {
    const nextType =
      nextView === "creatives"
        ? "image"
        : nextView === "videos"
          ? "video"
          : "website";
    navigate(`/portfolio/${nextType}`);
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  };

  const handleCategorySelect = (category: string) => {
    const hasVideos = videoGroups.some((item) => item.title === category);
    const hasCreatives = creativeGroups.some((item) => item.title === category);
    const type = hasVideos ? "video" : hasCreatives ? "image" : "website";

    navigate(getPortfolioCategoryPath(type, category), {
      state: { highlightedCategory: category },
    });
  };

  const handleGroupSelect = (group: PortfolioDataItem) => {
    navigate(getPortfolioBrandPath(group));
  };

  const handleCreativeSelect = (item: PortfolioItem) => {
    if (!selectedGroup) return;
    navigate(getPortfolioMediaPath(selectedGroup, item.mediaIndex));
  };

  const handleCreativeClose = () => {
    if (!selectedGroup) return;
    navigate(getPortfolioBrandPath(selectedGroup));
  };

  const handleBackToCategories = () => {
    handleViewChange(view);
  };

  const handleActiveVideoChange = useCallback(
    (mediaIndex: number) => {
      if (!selectedGroup) return;
      navigate(getPortfolioMediaPath(selectedGroup, mediaIndex), {
        replace: true,
      });
    },
    [navigate, selectedGroup],
  );

  if (view === "landing") {
    return (
      <div className="min-h-screen bg-[#0d1821] relative">
        <LandingNav />
        <PortfolioLanding
          groups={groups}
          onSelect={handleViewChange}
          onCategorySelect={handleCategorySelect}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1821] text-[#f5f5f5]">
      <PortfolioNav setView={handleViewChange} currentView={view} />

      <main className="mx-auto w-full max-w-[1432px] px-5 pb-28 pt-24 md:px-8 md:pb-12 lg:pt-32">
        {!selectedGroup ? (
          <CategoryBrowser
            groups={
              view === "creatives"
                ? creativeGroups
                : view === "videos"
                  ? videoGroups
                  : websiteGroups
            }
            mode={view}
            onSelect={handleGroupSelect}
            highlightedCategory={highlightedCategory}
            showTutorial={!tutorialSeen[view]}
            onTutorialSeen={() =>
              setTutorialSeen((current) => ({
                ...current,
                [view]: true,
              }))
            }
          />
        ) : view === "creatives" ? (
          <>
            <button
              type="button"
              onClick={handleBackToCategories}
              className="mb-6 rounded-lg border border-[#00a878]/50 px-4 py-3 font-poppins text-sm text-[#f5f5f5] outline-none ring-[#00a878] transition hover:border-[#00a878] hover:bg-[#00a878] focus-visible:ring-2"
            >
              <span className="flex items-center gap-2">
                <ArrowLeft className="h-5 w-5" />
                Back to Categories
              </span>
            </button>

            <CreativeGallery
              group={selectedGroup}
              onSelect={handleCreativeSelect}
            />
          </>
        ) : view === "videos" ? (
          <VideosFeed
            items={selectedItems}
            initialIndex={initialVideoIndex}
            onActiveIndexChange={handleActiveVideoChange}
            onBack={handleBackToCategories}
            group={selectedGroup}
          />
        ) : (
          <>
            <button
              type="button"
              onClick={handleBackToCategories}
              className="mb-6 rounded-lg border border-[#00a878]/50 px-4 py-3 font-poppins text-sm text-[#f5f5f5] outline-none ring-[#00a878] transition hover:border-[#00a878] hover:bg-[#00a878] focus-visible:ring-2"
            >
              <span className="flex items-center gap-2">
                <ArrowLeft className="h-5 w-5" />
                Back to Categories
              </span>
            </button>
            <WebsiteGallery group={selectedGroup} />
          </>
        )}
      </main>

      {selectedCreative && (
        <CreativeLightbox
          item={selectedCreative}
          group={selectedGroup}
          onClose={handleCreativeClose}
        />
      )}
    </div>
  );
};

export default Portfolio;
